import createClient from 'openapi-fetch';
import type { Middleware } from 'openapi-fetch';

const MAX_RETRIES = 3;
const BASE_DELAY = 1000;
const TIMEOUT_MS = 25000;

export interface ApiClientOptions {
  baseUrl: string;
  label?: string;
  headers?: Record<string, string>;
}

/**
 * Determines whether a thrown error is a transient network issue worth retrying
 * (e.g. connect timeout, DNS failure, socket hang-up).
 */
const isNetworkError = (error: any): boolean => {
  const code = error?.cause?.code ?? error?.code ?? '';
  return [
    'UND_ERR_CONNECT_TIMEOUT',
    'ECONNRESET',
    'ECONNREFUSED',
    'ETIMEDOUT',
    'UND_ERR_SOCKET',
  ].includes(code);
};

/**
 * Executes a single fetch with an AbortController timeout.
 * The timer is always cleaned up to avoid keeping the event loop alive.
 */
const fetchWithTimeout = async (
  url: string,
  init: RequestInit
): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
};

function createRetryMiddleware(label: string): Middleware {
  const timeoutIds = new WeakMap<Request, ReturnType<typeof setTimeout>>();

  return {
    async onRequest({ request }) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);
      const newRequest = new Request(request, { signal: controller.signal });
      timeoutIds.set(newRequest, timeoutId);
      return newRequest;
    },
    async onResponse({ request, response }) {
      const timeoutId = timeoutIds.get(request);
      if (timeoutId) clearTimeout(timeoutId);
      if (response.ok) return response;

      const isRetryable = response.status >= 500 || response.status === 429;
      if (!isRetryable) return response;

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        const delay = BASE_DELAY * Math.pow(2, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, delay));

        try {
          const retryResponse = await fetchWithTimeout(request.url, {
            method: request.method,
            headers: request.headers,
            body: request.body,
          });

          if (retryResponse.ok || attempt === MAX_RETRIES) {
            return retryResponse;
          }
        } catch (error: any) {
          if (attempt === MAX_RETRIES) throw error;
        }
      }

      return response;
    },
    async onError({ request, error }) {
      if (!isNetworkError(error)) throw error;

      const timeoutId = timeoutIds.get(request);
      if (timeoutId) clearTimeout(timeoutId);

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        const delay = BASE_DELAY * Math.pow(2, attempt - 1);
        const errorCode =
          (error as Error & { cause?: { code?: string } })?.cause?.code ??
          'unknown';
        console.warn(
          `⚠️ ${label} network error (${errorCode}), retry ${attempt}/${MAX_RETRIES} in ${delay}ms`
        );
        await new Promise(resolve => setTimeout(resolve, delay));

        try {
          return await fetchWithTimeout(request.url, {
            method: request.method,
            headers: request.headers,
            body: request.body,
          });
        } catch (retryError: any) {
          if (attempt === MAX_RETRIES) throw retryError;
        }
      }

      throw error;
    },
  };
}

/** Used when a process has no Datadog service tags, e.g. a local run. */
const FALLBACK_SERVICE = 'browse-sundeals-static-site-generator';

/** A full git SHA is noise in an access log; 12 characters is still unique. */
const VERSION_LENGTH = 12;

/**
 * The User-Agent sent by server-side requests made through `createApiClient`:
 * `{service}/{version}`, e.g.
 * `browse-sundeals-static-site-generator-price-cacher/1a2b3c4d5e6f`.
 *
 * Both parts come from the Datadog unified service tags (DD_SERVICE /
 * DD_VERSION), which Terraform already sets per deployable to the service name
 * and commit SHA. Reusing them means the name a downstream team sees in its
 * access logs is the same name our traces are filed under, and the version
 * points at the exact commit that sent the request.
 */
export const buildUserAgent = (
  env: Record<string, string | undefined>
): string => {
  const service = env.DD_SERVICE || ciService(env) || FALLBACK_SERVICE;
  const version = (env.DD_VERSION || env.CI_COMMIT_SHA || 'unknown').slice(
    0,
    VERSION_LENGTH
  );
  return `${service}/${version}`;
};

/**
 * CI builds (e.g. `npm run build:ci`, which runs the regen build on a GitLab
 * runner) call the live APIs without any Datadog tags, so they are named after
 * the job instead. The job name is reduced to a header-safe token, because a
 * space or colon would split or break the User-Agent.
 */
const ciService = (
  env: Record<string, string | undefined>
): string | undefined => {
  if (!env.CI_JOB_NAME) return undefined;
  const job = env.CI_JOB_NAME.replace(/[^A-Za-z0-9._-]+/g, '-');
  return `${FALLBACK_SERVICE}-ci-${job}`;
};

/**
 * Browsers own the User-Agent header (it cannot be replaced from a page), so it
 * is only set where the code runs on a server.
 */
const isServer = (): boolean =>
  typeof window === 'undefined' && typeof process !== 'undefined';

/**
 * Creates a typed openapi-fetch client with retry middleware and timeout handling.
 */
export function createApiClient<Paths extends {}>(options: ApiClientOptions) {
  const { baseUrl, label = 'API', headers } = options;

  const client = createClient<Paths>({
    baseUrl,
    headers: {
      'Content-Type': 'application/json',
      Connection: 'keep-alive',
      ...(isServer() ? { 'User-Agent': buildUserAgent(process.env) } : {}),
      ...headers,
    },
  });

  client.use(createRetryMiddleware(label));

  return client;
}
